import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ViewState,
  PassingForm,
  RelationshipType,
  AccountHolder,
  Beneficiary,
  NotificationDetail,
  ACCOUNT_HOLDER,
  CURRENT_BENEFICIARY,
  SECONDARY_BENEFICIARY,
  NOTIFICATION_DETAIL,
  RELATIONSHIP_OPTIONS,
  ACCESSIBLE_SECTIONS,
  COUNTDOWN_SECONDS,
  PEER_COUNTDOWN_LABEL,
} from './passing-notification.models';

@Component({
  selector:        'app-passing-notification',
  standalone:      true,
  imports:         [CommonModule, FormsModule],
  templateUrl:     './passing-notification.html',
  styleUrls:       ['./passing-notification.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PassingNotificationComponent implements OnInit, OnDestroy {

  // ─── Static data ────────────────────────────────────────────────────
  readonly accountHolder:        AccountHolder      = ACCOUNT_HOLDER;
  readonly currentBeneficiary:   Beneficiary        = CURRENT_BENEFICIARY;
  readonly secondaryBeneficiary: Beneficiary        = SECONDARY_BENEFICIARY;
  readonly notificationDetail:   NotificationDetail = NOTIFICATION_DETAIL;
  readonly relationshipOptions:  RelationshipType[] = RELATIONSHIP_OPTIONS;
  readonly accessibleSections                       = ACCESSIBLE_SECTIONS;
  readonly peerCountdownLabel                       = PEER_COUNTDOWN_LABEL;

  // ─── View state ──────────────────────────────────────────────────────
  currentView = signal<ViewState>('form');

  // ─── Form state ──────────────────────────────────────────────────────
  form: PassingForm = {
    dateOfPassing: '2026-05-20',
    relationship:  'Spouse / Partner',
    notes:         '',
  };

  // ─── Attestation state ───────────────────────────────────────────────
  attestChecked = signal<boolean>(false);

  canSubmitAttestation = computed(() => this.attestChecked());

  // ─── Countdown state ─────────────────────────────────────────────────
  remainingSeconds = signal<number>(COUNTDOWN_SECONDS - 2 * 60 - 38); // starts at ~71:57:22
  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  countdownDisplay = computed(() => {
    const total = this.remainingSeconds();
    const h     = Math.floor(total / 3600);
    const m     = Math.floor((total % 3600) / 60);
    const s     = total % 60;
    return `${String(h).padStart(2, '0')} : ${String(m).padStart(2, '0')} : ${String(s).padStart(2, '0')}`;
  });

  // ─── Form validation ─────────────────────────────────────────────────
  canSubmitForm = computed(() => {
    return !!this.form.dateOfPassing && !!this.form.relationship;
  });

  // ─── Formatted date for display ──────────────────────────────────────
  formattedDateOfPassing = computed(() => {
    if (!this.form.dateOfPassing) return '';
    const d = new Date(this.form.dateOfPassing);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  });

  // ─── Lifecycle ───────────────────────────────────────────────────────
  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.stopCountdown();
  }

  // ─── Navigation ──────────────────────────────────────────────────────
  goTo(view: ViewState): void {
    this.currentView.set(view);

    if (view === 'pending') {
      this.startCountdown();
    } else {
      this.stopCountdown();
    }

    if (view !== 'attestation') {
      this.attestChecked.set(false);
    }
  }

  isView(view: ViewState): boolean {
    return this.currentView() === view;
  }

  // ─── Form actions ─────────────────────────────────────────────────────
  submitForm(): void {
    if (!this.canSubmitForm()) return;
    this.goTo('pending');
  }

  cancelForm(): void {
    this.resetForm();
    this.goTo('form');
  }

  private resetForm(): void {
    this.form = {
      dateOfPassing: '',
      relationship:  '',
      notes:         '',
    };
  }

  // ─── Peer response actions ────────────────────────────────────────────
  confirmNotification(): void {
    this.goTo('access-unlocked');
  }

  disputeNotification(): void {
    this.goTo('access-frozen');
  }

  // ─── Attestation ─────────────────────────────────────────────────────
  toggleAttestation(): void {
    this.attestChecked.update(v => !v);
  }

  submitAttestation(): void {
    if (!this.canSubmitAttestation()) return;
    // In production: POST to /api/passing/attest with name, IP (server-captured), timestamp
    this.goTo('access-unlocked');
  }

  // ─── Countdown ────────────────────────────────────────────────────────
  private startCountdown(): void {
    this.stopCountdown();
    this.countdownInterval = setInterval(() => {
      this.remainingSeconds.update(s => (s > 0 ? s - 1 : 0));
      if (this.remainingSeconds() === 0) this.stopCountdown();
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────────
  trackByLabel(_: number, item: { label: string }): string {
    return item.label;
  }
}
